/**
 * Save / finalize pipeline for the report editor.
 *
 *  - `unsavedChanges` — compare editor state against `serverBaseline` to
 *    derive the dirty flag for the footer indicator + autosave + blocker
 *  - `refreshEditBaselineFromEditor` — snap the baseline to the just-saved
 *    editor state so `unsavedChanges` flips false immediately, before the
 *    RTK Query refetch lands and re-captures from the server payload
 *  - `handleSaveChange` / `handleFinalizeChange` — delegated to
 *    `useReportPersistence` so each hook stays within its LOC budget
 *
 * Payload assembly is delegated to `buildReportUpdatePayload` (pure util).
 * The hook receives an `editorSnapshot` rather than 10 discrete props so
 * the call site stays manageable.
 */

import { useCallback, useMemo } from 'react';
import type { notification } from 'antd';
import {
  FindingStatement,
  FollowUp,
  Report as MReport,
  ReportUpdatePayload,
} from '@/models/report';
import type { TemplateSection } from '@/models/template';
import {
  buildEffectiveFindingMap,
  captureReportEditBaseline,
  isReportEqualToBaseline,
  pruneReportUpdateToDelta,
  type ReportEditBaseline,
} from '../utils/reportEditBaseline';
import { buildReportUpdatePayload } from '../utils/buildReportUpdatePayload';
import { useReportPersistence } from './useReportPersistence';
import { useUncommittedText } from './useUncommittedText';

type NotificationApi = ReturnType<typeof notification.useNotification>[0];

type UnwrappableMutation<T> = (arg: T) => { unwrap: () => Promise<unknown> };

interface EditorSnapshot {
  currentReport: MReport | null;
  status: 'DRAFT' | 'PRELIMINARY' | 'FINAL';
  conclusion: { uuid?: string; value: string } | null;
  findings: Record<string, FindingStatement[]>;
  selectedFlags: Set<string>;
  followUp: FollowUp | null;
  serverBaseline: ReportEditBaseline | null;
  hasOpenAddendum: boolean;
}

interface UseReportSavePipelineArgs {
  snapshot: EditorSnapshot;
  setServerBaseline: (next: ReportEditBaseline) => void;
  buildFlagsPayload: () => { flagClass: string }[];
  tagsSection: TemplateSection | undefined;
  updateReport: UnwrappableMutation<ReportUpdatePayload>;
  finalizeReport: UnwrappableMutation<string>;
  logSave: (uuid: string) => void;
  notification: NotificationApi;
  addendumOpenedByEmail: string | undefined;
  addendumOpenedByName: string;
  activeUserEmail: string;
}

export interface ReportSavePipeline {
  unsavedChanges: boolean;
  /** Serialized editor content. Changes on every edit — feed to `useAutoSave`
   *  as `changeKey` so the debounce re-arms while `unsavedChanges` stays true. */
  contentKey: string;
  refreshEditBaselineFromEditor: () => void;
  handleSaveChange: () => Promise<boolean>;
  handleFinalizeChange: () => Promise<void>;
}

export function useReportSavePipeline({
  snapshot,
  setServerBaseline,
  buildFlagsPayload,
  tagsSection,
  updateReport,
  finalizeReport,
  logSave,
  notification: api,
  addendumOpenedByEmail,
  addendumOpenedByName,
  activeUserEmail,
}: UseReportSavePipelineArgs): ReportSavePipeline {
  const {
    currentReport,
    status,
    conclusion,
    findings,
    selectedFlags,
    followUp,
    serverBaseline,
    hasOpenAddendum,
  } = snapshot;

  const uncommittedText = useUncommittedText();
  const unsavedChanges = useMemo(() => {
    if (!serverBaseline || !currentReport) return false;
    if (uncommittedText) return true;
    return !isReportEqualToBaseline(serverBaseline, {
      currentReport,
      findingsState: findings,
      conclusion,
      selectedFlagClasses: Array.from(selectedFlags),
      followUp,
    });
  }, [
    serverBaseline,
    currentReport,
    findings,
    conclusion,
    selectedFlags,
    followUp,
    uncommittedText,
  ]);

  // A signature of the current editable content. It changes on every edit even
  // while `unsavedChanges` stays true, so autosave can re-arm its debounce.
  const contentKey = useMemo(() => {
    if (!currentReport) return '';
    const live = captureReportEditBaseline({
      currentReport,
      findingsState: findings,
      conclusion,
      selectedFlagClasses: Array.from(selectedFlags),
      followUp,
    });
    // Sort by key — the reconcile can reorder rows without changing content,
    // and an order-dependent key would falsely re-arm the autosave debounce.
    const sortByKey = (entries: Iterable<[string, string]>) =>
      Array.from(entries).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([
      sortByKey(live.measurementByCanonical.entries()),
      sortByKey(live.findingByClass.entries()),
      live.conclusionNormalized,
      live.conclusionUuid,
      live.flagsSorted,
      live.followUpSerialized,
      live.aiDeletedUuidsSorted,
      live.tagsSorted,
    ]);
  }, [currentReport, findings, conclusion, selectedFlags, followUp]);

  const resolvePayloadForSave = useCallback((): ReportUpdatePayload | null => {
    const full = buildReportUpdatePayload({
      currentReport,
      findings,
      conclusion,
      selectedFlagsPayload: buildFlagsPayload(),
      followUp,
      tagsSection,
      status,
      includeStatus: true,
    });
    if (!full) return null;
    if (!hasOpenAddendum || !serverBaseline || !currentReport) return full;
    return pruneReportUpdateToDelta({
      baseline: serverBaseline,
      fullPayload: full,
      effectiveFindingMap: buildEffectiveFindingMap(currentReport, findings),
      currentReport,
    });
  }, [
    currentReport,
    findings,
    conclusion,
    buildFlagsPayload,
    followUp,
    tagsSection,
    status,
    hasOpenAddendum,
    serverBaseline,
  ]);

  const refreshEditBaselineFromEditor = useCallback(() => {
    if (!currentReport) return;
    setServerBaseline(
      captureReportEditBaseline({
        currentReport,
        findingsState: findings,
        conclusion,
        selectedFlagClasses: Array.from(selectedFlags),
        followUp,
      })
    );
  }, [
    currentReport,
    findings,
    conclusion,
    selectedFlags,
    followUp,
    setServerBaseline,
  ]);

  const { handleSaveChange, handleFinalizeChange } = useReportPersistence({
    currentReport,
    resolvePayloadForSave,
    refreshEditBaselineFromEditor,
    updateReport,
    finalizeReport,
    logSave,
    notification: api,
    hasOpenAddendum,
    addendumOpenedByEmail,
    addendumOpenedByName,
    activeUserEmail,
  });

  return {
    unsavedChanges,
    contentKey,
    refreshEditBaselineFromEditor,
    handleSaveChange,
    handleFinalizeChange,
  };
}
